import styled from '@emotion/styled';
import { useSize } from 'ahooks';
import { Spin as ASpin } from 'antd';
import CEmpty from 'components/Empty/index';
import { initEcharts } from 'lib/echarts';
import { first, isEmpty } from 'lodash-es';
import dayjs from 'lib/dayjs';
import { formatNumber } from 'utils/numberFormat';
import { memo, useEffect, useMemo, useRef } from 'react';
import { TFnType, useTranslate } from '@tolgee/react';
import { PerformanceName, RankOfSection } from 'services/api/dashboard';
import CGroupLayout from './GroupLayout';
import { getEchartsNameByPerformanceName } from './utils';

const Empty = styled(CEmpty)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const GroupLayout = styled(CGroupLayout)`
  margin-bottom: 16px;
`;
const EchartsWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 24px;

  .echarts-group-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .el {
    height: 346px;
    width: 100%;
  }
`;
const Spin = styled(ASpin)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EchartsContainer = styled.div`
  width: 100%;
  height: 346px;
`;

interface EchartsGroupProps {
  data: RankOfSection | null | undefined;
  loading: boolean;
  unknownRank: boolean;
  performanceName?: PerformanceName;
  title: '我的指标' | '行业参考指标';
}

const toPercentNumber = (n: number) => n * 100;

function convertIndustryReference({ dailyRankOfSections: data }: RankOfSection, t: TFnType) {
  const dateRange = data?.map((x) => dayjs(x.date).format('MM-DD')) || [];
  return {
    grid: {
      left: '6%',
      right: '6%',
    },
    legend: {
      data: [
        {
          name: t('ViewDashboard.我的指标'),
          itemStyle: {
            color: '#5b8ff9',
          },
          lineStyle: {
            color: '#5b8ff9',
          },
        },
        {
          name: t('ViewDashboard.行业前10%店铺对应指标'),
          itemStyle: {
            color: '#5D7092',
          },
          lineStyle: {
            color: '#5D7092',
          },
        },
        {
          name: t('ViewDashboard.行业前25%店铺对应指标'),
          itemStyle: {
            color: '#faad14',
          },
          lineStyle: {
            color: '#faad14',
          },
        },
        {
          name: t('ViewDashboard.行业前50%店铺对应指标'),
          itemStyle: {
            color: '#E8684A',
          },
          lineStyle: {
            color: '#E8684A',
          },
        },
      ],
    },

    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dateRange,
    },
    yAxis: {
      type: 'value',
      max: (value: any) => {
        return value.max;
      },
      min: (value: any) => {
        return value.min;
      },
    },
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value: any) => formatNumber(Number(value) / 100, '0.00%'),
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      axisPointer: {
        type: 'line',
      },
    },
    series: [
      {
        data: data?.map((x) => toPercentNumber(x.selfPerformance)),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        name: t('ViewDashboard.我的指标'),
        lineStyle: {
          color: '#5b8ff9',
        },
        itemStyle: {
          color: '#5b8ff9',
        },
      },
      {
        data: data?.map((x) => toPercentNumber(x.top10)),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        name: t('ViewDashboard.行业前10%店铺对应指标'),
        lineStyle: {
          color: '#5D7092',
        },
        itemStyle: {
          color: '#5D7092',
        },
      },
      {
        data: data?.map((x) => toPercentNumber(x.top25)),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        lineStyle: {
          color: '#faad14',
        },
        itemStyle: {
          color: '#faad14',
        },
        name: t('ViewDashboard.行业前25%店铺对应指标'),
      },
      {
        data: data?.map((x) => toPercentNumber(x.top50)),
        type: 'line',
        smooth: true,
        symbol: 'circle',
        lineStyle: {
          color: '#E8684A',
        },
        itemStyle: {
          color: '#E8684A',
        },
        name: t('ViewDashboard.行业前50%店铺对应指标'),
      },
    ],
  };
}

function convertLeadStore(
  { dailyRankOfSections: data }: RankOfSection,
  t: TFnType,
  performanceName?: PerformanceName,
) {
  const dateRange = data?.map((x) => dayjs(x.date).format('MM-DD')) || [];
  const echartsNameList = getEchartsNameByPerformanceName(performanceName);
  return {
    grid: {
      left: '6%',
      right: '6%',
    },
    legend: {
      data: echartsNameList.map((p) => t(`ViewDashboard.${p}`)),
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },
    yAxis: [
      {
        type: 'value',
      },
      {
        type: 'value',
        name: '%',
        nameTextStyle: {
          align: 'left',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
    },

    series: [
      {
        data: data?.map((x) => x.selfPerformanceDenominator),
        type: 'bar',
        name: t(`ViewDashboard.${echartsNameList[0]}`),

        itemStyle: {
          color: '#5B8EF8',
          opacity: 0.85,
        },
      },
      {
        data: data?.map((x) => x.selfPerformanceNumerator),
        type: 'bar',
        name: t(`ViewDashboard.${echartsNameList[1]}`),
        itemStyle: {
          color: '#5bdaaa',
          opacity: 0.85,
        },
      },
      {
        data: data.map((x) => toPercentNumber(x.selfPerformance)),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        tooltip: {
          valueFormatter: (value: any) => formatNumber(Number(value) / 100, '0.00%'),
        },
        name: t(`ViewDashboard.${echartsNameList[2]}`),
        itemStyle: {
          color: '#f6903d',
        },
        lineStyle: {
          color: '#f6903d',
        },
      },
    ],
  };
}

function EchartsGroup(props: EchartsGroupProps) {
  const { data: echartsData, loading, title, unknownRank, performanceName } = props;
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};
  const { t } = useTranslate();
  const data = useMemo(() => {
    return unknownRank ? null : echartsData;
  }, [echartsData, unknownRank]);
  const echartsEmpty = useMemo(() => {
    if (unknownRank || !first(data?.dailyRankOfSections)) {
      return (
        <Empty description={t('ViewDashboard.该指标该行业下店铺数量较少，暂无行业排名数据')} />
      );
    }
    return null;
  }, [data, unknownRank, t]);
  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);
  useEffect(() => {
    if (!chartDomRef?.current || isEmpty(data?.dailyRankOfSections)) {
      chartRef?.current?.clear();
      return;
    }
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    const options =
      title === '我的指标'
        ? convertLeadStore(data!, t, performanceName)
        : convertIndustryReference(data!, t);
    chartRef.current.clear();
    chartRef.current.setOption(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, t, title]);

  return (
    <GroupLayout title={t(`ViewDashboard.${title}`)}>
      <Spin spinning={loading}>
        <EchartsWrapper>
          <div className="echarts-group-empty">{echartsEmpty}</div>
          <EchartsContainer ref={chartDomRef} />
        </EchartsWrapper>
      </Spin>
    </GroupLayout>
  );
}

export default memo(EchartsGroup);
